import type { Absence, BankHoliday, Booking, BookingType, Resource, WorkingHoursProfile } from '@/types';
import { format, isValid, parseISO, startOfDay, endOfDay } from 'date-fns';
import {
  calculateDayCapacity,
  getWorkingHoursForDay,
  isBankHoliday,
  isWorkingHoursProfileConfigured,
  normalizeDurationType,
  resolveBookingEnd,
  type PeriodUtilizationResult,
} from './heatmap-utils';

export interface ResourceBookingIndex {
  bookingsByResource: Map<string, Booking[]>;
  absencesByResource: Map<string, Absence[]>;
}

export function createResourceBookingIndex({
  bookings,
  absences,
}: {
  bookings: Booking[];
  absences: Absence[];
}): ResourceBookingIndex {
  const bookingsByResource = new Map<string, Booking[]>();
  const absencesByResource = new Map<string, Absence[]>();

  for (const booking of bookings) {
    const ids = new Set(
      [booking.resourceId, ...(booking.resourceIds ?? [])]
        .filter(id => id != null)
        .map(String),
    );
    for (const id of ids) {
      const resourceBookings = bookingsByResource.get(id) ?? [];
      resourceBookings.push(booking);
      bookingsByResource.set(id, resourceBookings);
    }
  }

  for (const absence of absences) {
    const id = String(absence.resourceId);
    const resourceAbsences = absencesByResource.get(id) ?? [];
    resourceAbsences.push(absence);
    absencesByResource.set(id, resourceAbsences);
  }

  return { bookingsByResource, absencesByResource };
}

interface PeriodInputs {
  resources: Resource[];
  bookings: Booking[];
  absences: Absence[];
  bankHolidays: BankHoliday[];
  bookingTypes: BookingType[];
  globalWorkingProfile: WorkingHoursProfile;
  startDate: Date;
  endDate: Date;
  index?: ResourceBookingIndex;
}

interface PeriodDay {
  date: Date;
  key: string;
}

interface WorkingHoursTemplate {
  minutesByDay: Map<string, number>;
  totalWorkingMinutes: number;
}

const dayKey = (date: Date): string => format(date, 'yyyy-MM-dd');

function forEachDayInRange(
  startDate: Date,
  endDate: Date,
  dayIndexes: Map<number, number>,
  visit: (dayIndex: number) => void,
): void {
  const firstDay = startOfDay(startDate);
  const lastDay = startOfDay(endDate);
  const startIndex = dayIndexes.get(firstDay.getTime());
  const endIndex = dayIndexes.get(lastDay.getTime());
  if (startIndex === undefined || endIndex === undefined || startIndex > endIndex) return;
  for (let index = startIndex; index <= endIndex; index++) {
    visit(index);
  }
}

function forEachBookingDay(
  booking: Booking,
  spanStart: Date,
  spanEnd: Date,
  dayIndexes: Map<number, number>,
  visit: (dayIndex: number) => void,
): void {
  const bookingStart = new Date(booking.startTime);
  const durationType = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
  const bookingEnd = resolveBookingEnd(booking, durationType !== 'hourly');
  if (!isValid(bookingStart) || !isValid(bookingEnd)) return;

  const firstCandidate = bookingStart > spanStart ? bookingStart : spanStart;
  const lastCandidate = bookingEnd < spanEnd ? bookingEnd : spanEnd;
  if (firstCandidate >= lastCandidate) return;

  const candidateStart = startOfDay(firstCandidate);
  const candidateEnd = startOfDay(new Date(lastCandidate.getTime() - 1));
  forEachDayInRange(candidateStart, candidateEnd, dayIndexes, visit);
}

function forEachAbsenceDay(
  absence: Absence,
  spanStart: Date,
  spanEnd: Date,
  dayIndexes: Map<number, number>,
  visit: (dayIndex: number) => void,
): void {
  const absenceStart = parseISO(absence.startDate);
  const absenceEnd = parseISO(absence.endDate);
  if (!isValid(absenceStart) || !isValid(absenceEnd)) return;

  const firstDay = absenceStart > spanStart ? absenceStart : spanStart;
  const lastDay = absenceEnd < spanEnd ? absenceEnd : spanEnd;
  if (firstDay > lastDay) return;
  forEachDayInRange(firstDay, lastDay, dayIndexes, visit);
}

/**
 * Groups items onto the period days they touch, keeping each day's items in
 * their original order.  The groups are a superset of what the per-day
 * capacity calculation keeps, so handing it one day's group instead of the
 * whole list gives the same result without rescanning every item per day.
 */
function groupByDay<T>(
  items: T[],
  dayCount: number,
  forEachDay: (item: T, visit: (dayIndex: number) => void) => void,
): Array<T[] | undefined> {
  const byDay = new Array<T[] | undefined>(dayCount);
  for (const item of items) {
    forEachDay(item, dayIndex => {
      const dayItems = byDay[dayIndex];
      if (dayItems) {
        dayItems.push(item);
      } else {
        byDay[dayIndex] = [item];
      }
    });
  }
  return byDay;
}

const NO_BOOKINGS: Booking[] = [];
const NO_ABSENCES: Absence[] = [];
const NO_BANK_HOLIDAYS: BankHoliday[] = [];

function calculatePeriodResult(
  resourceId: string,
  resource: Resource,
  bookings: Booking[],
  absences: Absence[],
  days: PeriodDay[],
  bankHolidays: BankHoliday[],
  bankHolidayDays: Set<string>,
  bookingTypes: BookingType[],
  getTemplate: (resource: Resource) => WorkingHoursTemplate,
  dayIndexes: Map<number, number>,
): PeriodUtilizationResult {
  const template = getTemplate(resource);
  const spanStart = days[0]?.date;
  const spanEnd = days[days.length - 1]?.date;
  let bookingsByDay: Array<Booking[] | undefined> = [];
  let absencesByDay: Array<Absence[] | undefined> = [];

  if (spanStart && spanEnd) {
    const spanEndOfDay = endOfDay(spanEnd);
    bookingsByDay = groupByDay(bookings, days.length, (booking, visit) =>
      forEachBookingDay(booking, spanStart, spanEndOfDay, dayIndexes, visit));
    absencesByDay = groupByDay(absences, days.length, (absence, visit) =>
      forEachAbsenceDay(absence, spanStart, spanEndOfDay, dayIndexes, visit));
  }

  let totalBookedMinutes = 0;
  let totalCapacityMinutes = template.totalWorkingMinutes;

  for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
    const day = days[dayIndex];
    const dayBookings = bookingsByDay[dayIndex];
    const dayAbsences = absencesByDay[dayIndex];
    const dayIsBankHoliday = bankHolidayDays.has(day.key);
    if (!dayBookings && !dayAbsences && !dayIsBankHoliday) continue;

    const workingHoursForDay = template.minutesByDay.get(day.key)! / 60;
    if (workingHoursForDay === 0) continue;

    const dayCapacity = calculateDayCapacity(
      resourceId,
      day.date,
      dayBookings ?? NO_BOOKINGS,
      workingHoursForDay,
      dayAbsences ?? NO_ABSENCES,
      dayIsBankHoliday ? bankHolidays : NO_BANK_HOLIDAYS,
      bookingTypes,
    );
    totalCapacityMinutes += dayCapacity.availableMinutes - dayCapacity.workingMinutes;
    totalBookedMinutes += dayCapacity.rawBookedMinutes;
  }

  const totalBookedHours = Math.round((totalBookedMinutes / 60) * 10) / 10;
  const totalCapacityHours = Math.round((totalCapacityMinutes / 60) * 10) / 10;
  const utilizationPercentage = totalCapacityHours > 0
    ? Math.min(999, Math.round((totalBookedHours / totalCapacityHours) * 100))
    : 0;

  return { totalBookedHours, totalCapacityHours, utilizationPercentage };
}

export function createResourcePeriodUtilisation(inputs: PeriodInputs) {
  const resourcesById = new Map(inputs.resources.map(resource => [String(resource.id), resource]));
  const index = inputs.index ?? createResourceBookingIndex(inputs);
  const days: PeriodDay[] = [];
  const currentDate = startOfDay(inputs.startDate);
  const endDate = startOfDay(inputs.endDate);
  const dayIndexes = new Map<number, number>();
  while (currentDate <= endDate) {
    dayIndexes.set(currentDate.getTime(), days.length);
    days.push({ date: new Date(currentDate), key: dayKey(currentDate) });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const bankHolidayDays = new Set<string>();
  if (inputs.bankHolidays.length > 0) {
    for (const day of days) {
      if (isBankHoliday(day.date, inputs.bankHolidays)) {
        bankHolidayDays.add(day.key);
      }
    }
  }

  const templates = new Map<string, WorkingHoursTemplate>();
  const getTemplate = (resource: Resource): WorkingHoursTemplate => {
    const hasCustomProfile = isWorkingHoursProfileConfigured(resource.customWorkingProfile);
    const profileKey = hasCustomProfile
      ? JSON.stringify(resource.customWorkingProfile)
      : 'global';
    const existing = templates.get(profileKey);
    if (existing) return existing;

    const minutesByDay = new Map<string, number>();
    let totalWorkingMinutes = 0;
    for (const day of days) {
      const workingHours = getWorkingHoursForDay(resource, day.date, inputs.globalWorkingProfile);
      const workingMinutes = Math.max(0, workingHours * 60);
      minutesByDay.set(day.key, workingMinutes);
      totalWorkingMinutes += workingMinutes;
    }
    const template = { minutesByDay, totalWorkingMinutes };
    templates.set(profileKey, template);
    return template;
  };

  const results = new Map<string, PeriodUtilizationResult>();

  return (resourceId: string): PeriodUtilizationResult | undefined => {
    const resource = resourcesById.get(resourceId);
    if (!resource) return undefined;

    let result = results.get(resourceId);
    if (!result) {
      result = calculatePeriodResult(
        resourceId,
        resource,
        index.bookingsByResource.get(resourceId) ?? [],
        index.absencesByResource.get(resourceId) ?? [],
        days,
        inputs.bankHolidays,
        bankHolidayDays,
        inputs.bookingTypes,
        getTemplate,
        dayIndexes,
      );
      results.set(resourceId, result);
    }
    return result;
  };
}
